import ActionTypes from '../actions/types'
import { createReducer } from 'reduxsauce'
import Utils from '../utils/Utils'

// Copied from AlarmUtils. We don't update the lastUpdated timestamp in this one. Keep in sync.
const updateAlarmParticipant = (alarm, participantId, update) => {
  if (
    alarm.backupGroup &&
    Utils.getObjectWithId(alarm.backupGroup.members, participantId)
  ) {
    const member = Utils.getObjectWithId(
      alarm.backupGroup.members,
      participantId
    )
    const memberIndex = Utils.getIndexOfObjectWithId(
      alarm.backupGroup.members,
      participantId
    )
    const newMember = {
      ...member,
      ...update
    }
    const newMembers = alarm.backupGroup.members.map((m, index) =>
      index === memberIndex ? newMember : m
    )
    return {
      ...alarm,
      backupGroup: {
        ...alarm.backupGroup,
        members: newMembers
      }
    }
  } else if (Utils.getObjectWithId(alarm.backupContacts, participantId)) {
    const backupContact = Utils.getObjectWithId(
      alarm.backupContacts,
      participantId
    )
    const backupContactIndex = Utils.getIndexOfObjectWithId(
      alarm.backupContacts,
      participantId
    )
    const newBackupContact = {
      ...backupContact,
      ...update
    }
    const newBackupContacts = alarm.backupContacts.map((c, index) =>
      index === backupContactIndex ? newBackupContact : c
    )
    return {
      ...alarm,
      backupContacts: newBackupContacts
    }
  } else if (alarm.recipient && alarm.recipient.id === participantId) {
    return {
      ...alarm,
      recipient: {
        ...alarm.recipient,
        ...update
      }
    }
  } else {
    return alarm
  }
}

const INITIAL_STATE = {
  alarms: []
}

const addAlarm = (state, action) => {
  const { alarmId, alarm } = action.payload
  const alarmIndex = Utils.getIndexOfObjectWithId(state.alarms, alarmId)

  let newAlarms

  // New backup alarm
  if (alarmIndex === -1) {
    newAlarms = [...state.alarms, alarm]
  } else {
    // Update existing alarm
    newAlarms = state.alarms.map((a, index) =>
      index === alarmIndex ? alarm : a
    )
  }

  return {
    ...state,
    alarms: newAlarms
  }
}

const removeAlarm = (state, action) => {
  const { alarmId } = action.payload
  let alarmIndex = Utils.getIndexOfObjectWithId(state.alarms, alarmId)
  if (alarmIndex !== -1) {
    const newAlarms = state.alarms.filter((_, index) => index !== alarmIndex)
    return {
      ...state,
      alarms: newAlarms
    }
  } else {
    console.log('Trying to delete a non-existing backup alarm')
    return state
  }
}

const removeAlarms = (state, action) => {
  const alarmIds = action.payload.alarmIds
  const newAlarms = state.alarms.filter(alarm => !alarmIds.includes(alarm.id))
  return {
    ...state,
    alarms: newAlarms
  }
}

const expireAlarms = (state, action) => {
  const alarmIds = action.payload.alarmIds
  if (alarmIds.length === 0) {
    return state
  }

  // Don't update lastUpdatedAt because it will cause
  // the alarm to be synced.
  const newAlarms = state.alarms.map(alarm => {
    if (alarmIds.includes(alarm.id)) {
      return {
        ...alarm,
        lastExpiredAt: alarm.lastExpiredAt ? null : Date.now()
      }
    }
    return alarm
  })

  return {
    ...state,
    alarms: newAlarms
  }
}

const updateAlarmsLocally = (state, action) => {
  const alarmIds = action.payload.alarmIds
  if (alarmIds.length === 0) {
    return state
  }

  // This is used to move the alarms from one section to another.
  // We change a property locally that is not synced with fireabse
  const newAlarms = state.alarms.map(alarm => {
    if (alarmIds.includes(alarm.id)) {
      return {
        ...alarm,
        lastSeenAt: alarm.lastSeenAt ? null : Date.now()
      }
    }
    return alarm
  })

  return {
    ...state,
    alarms: newAlarms
  }
}

const unsnoozeAlarmsLocally = (state, action) => {
  const alarmIds = action.payload.alarmIds
  const uid = action.payload.uid
  if (alarmIds.length === 0) {
    return state
  }

  const newAlarms = state.alarms.map(alarm => {
    if (alarmIds.includes(alarm.id)) {
      return updateAlarmParticipant(alarm, uid, {
        lastSnoozeOn: null,
        lastSnoozeFor: null
      })
    }
    return alarm
  })

  return {
    ...state,
    alarms: newAlarms
  }
}

// Soft updates don't change the persistedOnline flag such that they are not
// colored grey in the app
const softUpdateLocalAlarm = (state, action) => {
  const alarm = action.payload.alarm
  const alarmIndex = Utils.getIndexOfObjectWithId(state.alarms, alarm.id)
  if (alarmIndex !== -1) {
    const newAlarms = state.alarms.map((a, index) =>
      index === alarmIndex ? alarm : a
    )
    return {
      ...state,
      alarms: newAlarms
    }
  } else {
    console.log('Trying to edit a non-existent alarm')
    return state
  }
}

const ACTION_HANDLERS = {
  [ActionTypes.ADD_BACKUP_ALARM]: addAlarm,
  [ActionTypes.REMOVE_BACKUP_ALARM]: removeAlarm,
  [ActionTypes.REMOVE_PARTICIPANT_ALARMS]: removeAlarms,
  [ActionTypes.EXPIRE_PARTICIPANT_ALARMS]: expireAlarms,
  [ActionTypes.UPDATE_PARTICIPANT_ALARMS_LOCALLY]: updateAlarmsLocally,
  [ActionTypes.UNSNOOZE_PARTICIPANT_ALARMS_LOCALLY]: unsnoozeAlarmsLocally,
  [ActionTypes.SOFT_UPDATE_LOCAL_PARTICIPANT_ALARM]: softUpdateLocalAlarm
}

export default createReducer(INITIAL_STATE, ACTION_HANDLERS)
